import * as Sentry from '@sentry/react';

// Enums
import { EnvType } from './enums/common';

const SENTRY_NODE_ENV = import.meta.env.VITE_APP_SENTRY_NODE_ENV;
const SENTRY_DSN = import.meta.env.VITE_APP_SENTRY_DSN;

// Sentry.browserTracingIntegration() - Track performance and routing in the app.
// Sentry.replayIntegration() - Capture user session replays for errors.

// Base Sentry configuration shared across environments
const BASE_SENTRY_OPTIONS = {
  dsn: SENTRY_DSN, // The DSN (Data Source Name) to identify your project on Sentry.
  enabled: true, // Enables Sentry in all environments unless overridden.
  integrations: [], // We’ll add specific integrations based on the environment.
};

// Sentry config per environment
const SENTRY_CONFIG = {
  [EnvType.LOCAL]: {
    // Local environment, no errors will be sent to Sentry.
    dsn: '', // Empty DSN, no events will be captured.
    tracesSampleRate: 0.0, // No performance data captured in local environment.
    replaysSessionSampleRate: 0.0, // No session replay in local.
    replaysOnErrorSampleRate: 0.0, // User sessions are not recorded during errors.
    environment: EnvType.LOCAL, // Environment set to 'LOCAL'.
    enabled: false, // Disable Sentry for local development.
  },
  [EnvType.DEV]: {
    // Development environment, we track some performance and errors, but not too much.
    tracesSampleRate: 0.1, // Capture 10% of performance traces.
    replaysSessionSampleRate: 0.1, // Record 10% of user sessions.
    replaysOnErrorSampleRate: 0.2, // Record 20% of user sessions when errors happen.
    environment: EnvType.DEV, // Environment set to 'DEV'.
  },
  [EnvType.PROD]: {
    // Production environment, higher sample rates for better error tracking.
    tracesSampleRate: 0.2, // Capture 20% of performance traces (more data in production).
    replaysSessionSampleRate: 0.05, // Record 5% of user sessions for performance analysis.
    replaysOnErrorSampleRate: 1.0, // Record all user sessions when errors happen.
    environment: EnvType.PROD, // Environment set to 'PROD'.
  },
};


const getSentryOptions = (env: EnvType) => {
  switch (env) {
  case EnvType.LOCAL:
    return SENTRY_CONFIG[EnvType.LOCAL];
  case EnvType.DEV:
    return {
      ...SENTRY_CONFIG[EnvType.DEV],
      ...BASE_SENTRY_OPTIONS,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
    };
  case EnvType.PROD:
    return {
      ...SENTRY_CONFIG[EnvType.PROD],
      ...BASE_SENTRY_OPTIONS,
      integrations: [Sentry.replayIntegration()],
    };
  default:
    throw new Error(`Unknown SENTRY_NODE_ENV: ${SENTRY_NODE_ENV}`);
  }
};

const sentryOptions = getSentryOptions(SENTRY_NODE_ENV);

Sentry.init(sentryOptions);
